import React, { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Box, Container } from '@mui/material'
import {
  Header,
  Footer,
  LoginStatus,
  DonationSidebar,
} from '@/components/layout'
import { ROUTES } from '@/constants'
import { useDonorContext, useTenantContext } from '@/context'
import {
  getPrimaryDonationNavItems,
  isPrimaryDonationSchedulingPath,
} from '@/utils/primary-donation-nav'

export const DonateLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { donor } = useDonorContext()
  const { tenant, loadingTenant } = useTenantContext()

  const navItems = useMemo(() => getPrimaryDonationNavItems(tenant), [tenant])

  useEffect(() => {
    if (loadingTenant) return
    if (navItems.length > 0) return
    if (location.pathname === ROUTES.DONATE_NAV_NOT_CONFIGURED) return
    if (!isPrimaryDonationSchedulingPath(location.pathname)) return
    navigate(ROUTES.DONATE_NAV_NOT_CONFIGURED, { replace: true })
  }, [loadingTenant, navItems.length, location.pathname, navigate])

  const selectedId = useMemo(() => {
    const current = navItems.find(item =>
      location.pathname.startsWith(item.path)
    )
    return current?.id
  }, [location.pathname, navItems])

  const handleNavChange = id => {
    const target = navItems.find(x => x.id === id)
    if (target && location.pathname !== target.path) {
      navigate(target.path)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box
          component='aside'
          sx={{
            display: 'flex',
            p: { xs: 1.5, md: 3 },
            position: 'relative',
            minWidth: { xs: '100%', md: 300, lg: 340 },
            flexDirection: { xs: 'column-reverse', md: 'column' },
          }}
        >
          {navItems.length > 0 ? (
            <DonationSidebar
              title='Where to Donate:'
              items={navItems.map(({ id, label }) => ({ id, label }))}
              selectedId={selectedId}
              onChange={handleNavChange}
              autoCollapse={location.pathname.startsWith(
                ROUTES.SCHEDULE_DONATION
              )}
            />
          ) : null}
          <Box sx={{ mt: { xs: 0, md: 2 }, mb: { xs: 2, md: 0 } }}>
            <LoginStatus id='donation-login-status' donor={donor} />
          </Box>
        </Box>
        <Container
          component='main'
          maxWidth='xl'
          sx={{
            py: { xs: 0.5, md: 3 },
            flex: 1,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Outlet />
        </Container>
      </Box>
      <Footer />
    </Box>
  )
}
